'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _extends = require('@babel/runtime/helpers/extends');
var _defineProperty = require('@babel/runtime/helpers/defineProperty');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var _objectWithoutProperties = require('@babel/runtime/helpers/objectWithoutProperties');
var React = require('react');
var react = require('@chakra-ui/react');
var ReactSelectAsync = require('react-select/async');
var debounce = require('lodash/debounce.js');
var CustomReactSelect = require('./base/CustomReactSelect.js');
var useScript = require('./base/useScript.js');
var getReactSelectConfiguration = require('./base/getReactSelectConfiguration.js');
var logger = require('../../utils/logger.js');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var _defineProperty__default = /*#__PURE__*/_interopDefaultLegacy(_defineProperty);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var _objectWithoutProperties__default = /*#__PURE__*/_interopDefaultLegacy(_objectWithoutProperties);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var ReactSelectAsync__default = /*#__PURE__*/_interopDefaultLegacy(ReactSelectAsync);
var debounce__default = /*#__PURE__*/_interopDefaultLegacy(debounce);
var _excluded = ["onChange", "defaultValue", "type", "country", "filter", "googlePlacesApiKey"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty__default["default"](target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

// Possible filter values

/**
 * Select with Google Places
 *
 * A custom select with Google's "Places" autocompletion.
 */
var SelectWithGooglePlaces = react.forwardRef(function (_ref, ref) {
  var onChange = _ref.onChange,
    defaultValue = _ref.defaultValue,
    type = _ref.type,
    _ref$country = _ref.country,
    country = _ref$country === void 0 ? ['de'] : _ref$country,
    filter = _ref.filter,
    googlePlacesApiKey = _ref.googlePlacesApiKey,
    props = _objectWithoutProperties__default["default"](_ref, _excluded);
  // Load script - but only if the script or `window.google.maps.places` doesn't exist already
  var _useScript = useScript["default"]({
      src: "https://maps.googleapis.com/maps/api/js?libraries=places&key=".concat(googlePlacesApiKey),
      // Check if it was already loaded.
      checkForExisting: true,
      // Do not load script if it's already there.
      shouldLoad: function shouldLoad() {
        return !(window.google && window.google.maps && window.google.maps.places);
      }
    }),
    _useScript2 = _slicedToArray__default["default"](_useScript, 1),
    loading = _useScript2[0];
  var placesNode = React.useRef(null);
  var _useState = React.useState(null),
    _useState2 = _slicedToArray__default["default"](_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];

  // Create `placesService` instance
  var placesService = React.useMemo(function () {
    if (loading) {
      return null;
    }
    return new window.google.maps.places.PlacesService(placesNode.current);
  }, [loading]);

  // Create `autocomplete` instance
  var autocompleteService = React.useMemo(function () {
    if (loading) {
      return null;
    }
    return new window.google.maps.places.AutocompleteService();
  }, [loading]);

  /**
   * Get place predictions via query (autocomplete)
   * @param {string} query
   * @return {Promise<array>}
   */
  var getPlacePredictions = function getPlacePredictions(query) {
    return new Promise(function (resolve, reject) {
      /**
       * Map google places prediction to select option
       * @param prediction
       * @return {{label: string, value: string}}
       */
      var toOption = function toOption(prediction) {
        return {
          label: prediction.description,
          value: prediction.structured_formatting.main_text,
          placeId: prediction.place_id,
          structuredFormatting: prediction.structured_formatting
        };
      };
      autocompleteService.getPlacePredictions({
        input: query,
        // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest
        types: type && [type],
        // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#ComponentRestrictions
        componentRestrictions: {
          country: country
        }
      }, function (predictions, status) {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          reject(new Error("getPlacePredictions failed with status \"".concat(status, "\".")));
          return;
        }
        if (predictions) {
          if (filter) {
            resolve(
            // Filter predictions according to the `filter` prop.
            // Checks if the given prediction contains some of the types in `filter`.
            predictions.filter(function (prediction) {
              return prediction.types.some(function (predictionType) {
                return filter.indexOf(predictionType) !== -1;
              });
            }).map(toOption));
          } else {
            resolve(predictions.map(toOption));
          }
        }
      });
    });
  };

  /**
   * Get a place's details via `placeId`
   *
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceDetailsRequest
   * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult
   * @param {string} placeId
   * @return {Promise<object>}
   */
  var getPlaceDetails = function getPlaceDetails(placeId) {
    return new Promise(function (resolve, reject) {
      placesService.getDetails({
        placeId: placeId,
        // We only need `address_components` for now.
        // https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceDetailsRequest.fields
        fields: ['address_components', 'formatted_address']
      }, function (placeInfo, status) {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          reject(new Error("getPlaceDetails failed with status \"".concat(status, "\".")));
          return;
        }
        resolve(placeInfo);
      });
    });
  };

  // Default value - get place information and set the label in a consistent way.
  React.useEffect(function () {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!loading && defaultValue !== null && defaultValue !== void 0 && defaultValue.value) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      getPlaceDetails(defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.value).then(function (placeInfo) {
        setValue({
          label: placeInfo.formatted_address,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          value: defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.value
        });
      })["catch"](function () {
        // `getPlaceDetails` failed, set defaultValue "as is".
        setValue(defaultValue);
      });
    }
  }, [loading]);
  var loadOptions = function loadOptions(inputValue, callback) {
    if (inputValue) {
      if (autocompleteService) {
        getPlacePredictions(inputValue).then(function (options) {
          callback(options);
        })["catch"](function (error) {
          logger.warn(error, 'SelectWithGooglePlaces');
        });
      }
    }
  };

  /**
   * Handles the select's `change` event and is responsible for setting the
   * chosen value's label and passing the place information to `onChange`.
   * @param option
   * @param action
   */
  var handleChange = function handleChange(option, action) {
    // If option is empty (i.e. it was cleared), clear the value.
    if (!option) {
      onChange(null, action);
      setValue(null);
      return;
    }

    // "Active" (chosen) options should have a shorter, better readable label.
    // Example: `Kollaustraße 119, Hamburg, Germany` => `Kollaustraße 119`.
    var activeOption = {
      value: option.value,
      label: option.structuredFormatting.main_text,
      placeId: option.placeId
    };
    setValue(activeOption);
    if (option !== null && option !== void 0 && option.value && typeof onChange === 'function') {
      getPlaceDetails(option.placeId).then(function (placeInfo) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange({
          option: activeOption,
          action: action,
          placeInfo: placeInfo
        });
      });
    }
  };
  return /*#__PURE__*/React__default["default"].createElement(React.Fragment, null, /*#__PURE__*/React__default["default"].createElement(CustomReactSelect["default"], _extends__default["default"]({
    value: value,
    valueKey: "label",
    loadOptions: debounce__default["default"](loadOptions, 200),
    onChange: handleChange,
    components: _objectSpread(_objectSpread({}, getReactSelectConfiguration.components), {}, {
      DropdownIndicator: null
    })
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,

    reactSelectComponent: ReactSelectAsync__default["default"]
  }, props, {
    ref: ref
  })), /*#__PURE__*/React__default["default"].createElement("div", {
    ref: placesNode
  }));
});
SelectWithGooglePlaces.displayName = 'Select.WithGooglePlaces';
exports.SelectWithGooglePlaces = SelectWithGooglePlaces;